import BigNumber from 'bignumber.js';
import { Copy } from 'lucide-react';
import { Button, Confirm, useDisclosure, useNavigate, useQueryState, useMutation, useQuery, useI18n } from '@ywwwtseng/react-kit';
import { Typography } from '@ywwwtseng/tma-react-kit';
import { Users, ChevronRight } from 'lucide-react';
import * as web3 from '@ywwwtseng/web3';
import * as schema from '@/db/schema';
import * as utils from '@/utils';
import { CountdownTimer } from '@/components/common/CountdownTimer';
import type { Gift, GiftMetadata } from '@/types';

export default function GiftScreen({
  params,
}: {
  params: { gift_id: string };
}) {
  const { isOpen, onOpenChange } = useDisclosure();
  const me = useQueryState<typeof schema.users.$inferSelect>('me');
  const { data: gift } = useQuery<Gift>('user_gift', {
    params: {
      id: params.gift_id,
    },
    enabled: !!me?.id,
    refetchOnMount: true,
  });

  const { t } = useI18n();
  const navigate = useNavigate();
  const { mutate: closeGift, isLoading: isCloseGiftLoading } =
    useMutation('gift:close');
  const { mutate: resendGift, isLoading: isResendGiftLoading } =
    useMutation('gift:resend');

  if (!gift) {
    return (
      <div role="status" className="py-3 px-5">
        <div className="h-2.5 rounded-full bg-gray-700 w-48 mb-4"></div>
        <div className="h-2 rounded-full bg-gray-700 mb-2.5"></div>
        <div className="h-2 rounded-full bg-gray-700 mb-2.5"></div>
        <div className="h-2 rounded-full bg-gray-700"></div>

        <div className="border-b border-solid border-border my-4"></div>
        <div className="flex items-center">
          <div className="flex flex-col gap-2">
            <div className="w-48 h-2 rounded-full bg-gray-700"></div>
            <div className="w-40 h-2 rounded-full bg-gray-700"></div>
          </div>
        </div>
        <span className="sr-only">Loading...</span>
      </div>
    );
  }

  const decimals = Number(gift.token_id.split(':')[1]);
  const metadata = gift.metadata as GiftMetadata;
  const showApiInfo = gift.claim_type === 'api';

  const handleCopy = (value: string) => {
    if (!value) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(value).catch((error) => {
        console.error('Copy failed', error);
      });
    }
  };

  return (
    <div className="flex flex-col justify-start w-full h-full gap-2 py-3 px-5">
      {gift.image && (
        <img
          src={gift.image}
          alt={gift.description}
          className="bg-card object-contain w-full aspect-640/360 rounded-lg overflow-hidden"
        />
      )}

      <div className="card flex flex-col justify-start w-full">
        <Typography whitespacePreWrap size="1">
          {gift.description}
        </Typography>
      </div>

      <div className="card flex flex-col justify-start w-full gap-2">
        {gift.end_at && (
          <div className="flex items-center justify-between">
            <Typography
              whitespacePreWrap
              size="1"
              weight={600}
              i18n="gift.time_left"
            />
            <CountdownTimer gift={gift} />
          </div>
        )}

        <div className="border-b border-solid border-border"></div>

        <div className="w-full flex items-center justify-between py-0.25">
          <Typography size="1" weight={600} i18n="gift.total_amount" />
          <Typography size="1" weight={600}>
            {`${web3.utils.formatUnits(
              gift.total,
              decimals
            )} ${utils.token.getTokenSymbol(gift.token_id)}`}
          </Typography>
        </div>

        <div className="border-b border-solid border-border"></div>

        {new BigNumber(gift.refunded ?? '0').gt(0) && (
          <>
            <div className="w-full flex items-center justify-between py-0.25">
              <Typography size="1" weight={600} i18n="common.refunded" />
              <Typography size="1" weight={600}>
                {`${web3.utils.formatUnits(
                  gift.refunded ?? '0',
                  decimals
                )} ${utils.token.getTokenSymbol(gift.token_id)}`}
              </Typography>
            </div>
            <div className="border-b border-solid border-border"></div>
          </>
        )}

        <div className="w-full flex items-center justify-between py-0.25">
          <Typography size="1" weight={600} i18n="gift.ctr" />
          <Typography size="1" weight={600}>
            {gift.ctr > 0 ? gift.ctr : '-'}
          </Typography>
        </div>

        <div className="border-b border-solid border-border"></div>

        <div className="w-full flex items-center justify-between py-0.25">
          <Typography size="1" weight={600} i18n="gift.claimed_rate" />
          <Typography size="1" weight={600}>
            {gift.ctr > 0 ? `${new BigNumber(gift.recipients).dividedBy(gift.ctr).multipliedBy(100).toFixed(2)}%` : '-'}
          </Typography>
        </div>
        <div className="border-b border-solid border-border"></div>

        <div
          className="flex items-center gap-1 py-0.25"
          onClick={() => {
            navigate('GiftActivity', { params: { gift_id: params.gift_id } });
          }}
        >
          <Users size={14} strokeWidth={2} />
          <Typography size="1" weight={600}>
            {gift.recipients}
          </Typography>
          <Typography size="1" weight={600} i18n="common.received" />
          <Typography size="1" weight={600} className="ml-auto">
            {web3.utils.formatUnits(
              new BigNumber(gift.total).minus(gift.balance).toFixed(),
              decimals
            )}
          </Typography>
          <Typography size="1" weight={600}>
            {utils.token.getTokenSymbol(gift.token_id)}
          </Typography>
          <ChevronRight
            className="text-icon-foreground"
            size={18}
            strokeWidth={1.5}
          />
        </div>
      </div>



      {showApiInfo && (
        <div className="flex flex-col gap-2">
          <Typography size="1" weight={600}>
            {t('gift.api_access')}
          </Typography>
          <div className="w-full card px-3 py-3 flex flex-col gap-2">
            {metadata.api_secret && (
              <div className="flex flex-col gap-0.5">
                <Typography size="1" className="text-muted-foreground">
                  API Secret
                </Typography>
                <div className="flex items-center gap-3">
                  <div className="flex-1 flex items-center gap-2">
                    <Typography
                      size="1"
                      className="font-mono text-primary break-all"
                    >
                      {metadata.api_secret}
                    </Typography>
                  </div>
                  <Button
                    size="xs"
                    color="primary"
                    onClick={() => handleCopy(metadata.api_secret!)}
                  >
                    <Copy size={12} />
                  </Button>
                </div>
              </div>
            )}

            {metadata.api_url && (
              <div className="flex flex-col gap-0.5">
                <Typography size="1" className="text-muted-foreground">
                  API URL
                </Typography>
                <div className="flex items-center gap-3">
                  <div className="flex-1 flex items-center gap-2">
                    <Typography
                      size="1"
                      className="font-mono text-primary break-all"
                    >
                      {metadata.api_url}
                    </Typography>
                  </div>
                  <Button
                    size="xs"
                    color="primary"
                    onClick={() => handleCopy(metadata.api_url!)}
                  >
                    <Copy size={12} />
                  </Button>
                </div>
              </div>
            )}

            {metadata.api_key && (
              <div className="flex flex-col gap-0.5">
                <Typography size="1" className="text-muted-foreground">
                  API KEY
                </Typography>
                <div className="flex items-center gap-3">
                  <div className="flex-1 flex items-center gap-2">
                    <Typography
                      size="1"
                      className="font-mono text-primary break-all"
                    >
                      {metadata.api_key}
                    </Typography>
                  </div>
                  <Button
                    size="xs"
                    color="primary"
                    onClick={() => handleCopy(metadata.api_key!)}
                  >
                    <Copy size={12} />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col items-center justify-end gap-4 flex-1">
        {!gift.message_id && (
          <Button
            isLoading={isResendGiftLoading}
            size="sm"
            onClick={() => {
              void resendGift({ gift_id: params.gift_id }).then((result) => {
                if (result.ok) {
                  navigate(-1);
                }
              });
            }}
          >
            {t('common.resend')}
          </Button>
        )}
        {gift.status === 'active' && (
          <Button
            color={isCloseGiftLoading ? 'white' : 'destructive'}
            size="sm"
            isLoading={isCloseGiftLoading}
            onClick={() => {
              onOpenChange(true);
            }}
          >
            {t('common.cancel')}
          </Button>
        )}
      </div>
      <Confirm
        open={isOpen}
        onOpenChange={onOpenChange}
        title={t('cancel_gift_modal.title')}
        description={t('cancel_gift_modal.description')}
        cancel={{
          children: t('common.cancel'),
        }}
        confirm={{
          children: t('common.confirm'),
          onClick: () => {
            void closeGift({ gift_id: params.gift_id }).then((result) => {
              if (result.ok) {
                navigate(-1);
              }
            });
          },
        }}
      />
    </div>
  );
}
